import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/use-toast';
import { BookOpen, Bell, Mail } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

const ComingSoonPage = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNotifyMe = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;

    setIsSubmitting(true);

    // 1. Save the email to the waitlist (idempotent — unique constraint on email)
    const { error: insertError } = await supabase
      .from('waitlist' as never)
      .insert({ email: trimmed, source: 'coming_soon' } as never);

    // Duplicate email is fine — still let the user know they're on the list
    const alreadyOnList = insertError?.code === '23505';
    if (insertError && !alreadyOnList) {
      console.error('Waitlist insert failed:', insertError);
      toast({
        title: 'Could not save your email',
        description: 'Please try again in a moment.',
        variant: 'destructive',
      });
      setIsSubmitting(false);
      return;
    }

    // 2. Send a confirmation email (fire and forget — don't block the success toast)
    if (!alreadyOnList) {
      supabase.functions.invoke('send-email', {
        body: { template: 'waitlist_confirmation', to: trimmed, data: {} },
      }).catch(err => console.warn('Confirmation email send failed (non-fatal):', err));
    }

    toast({
      title: alreadyOnList ? "You're already on the list" : "You're on the list!",
      description: alreadyOnList
        ? "We'll email you when ChapterSeed launches."
        : "Check your inbox for a confirmation, and we'll let you know the moment we launch.",
    });
    setEmail('');
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex flex-col items-center justify-center p-6">
      <div className="max-w-2xl mx-auto text-center space-y-8">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <BookOpen className="h-12 w-12 text-chapterly-purple" />
          <h1 className="font-playfair text-4xl md:text-5xl font-bold text-foreground">
            ChapterSeed
          </h1>
        </div>

        {/* Main Content */}
        <div className="space-y-4">
          <h2 className="font-playfair text-3xl md:text-4xl font-semibold text-foreground">
            Reading is how imagination grows
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl max-w-lg mx-auto">
            We're building a home for stories you build in your head — the kind streaming can't give you.
          </p>
        </div>

        {/* Email Signup */}
        <form onSubmit={handleNotifyMe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto mt-8">
          <div className="relative flex-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-10 h-12"
              required
            />
          </div>
          <Button 
            type="submit" 
            className="h-12 px-6 bg-chapterly-purple hover:bg-chapterly-purple/90"
            disabled={isSubmitting}
          >
            <Bell className="h-4 w-4 mr-2" />
            {isSubmitting ? "Subscribing..." : "Notify Me"}
          </Button>
        </form>

        {/* Features Preview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16 pt-8 border-t border-border">
          <div className="space-y-2">
            <div className="w-12 h-12 rounded-full bg-chapterly-purple/10 flex items-center justify-center mx-auto">
              <BookOpen className="h-6 w-6 text-chapterly-purple" />
            </div>
            <h3 className="font-playfair font-semibold">Endless Stories</h3>
            <p className="text-sm text-muted-foreground">Discover serialized stories across every genre</p>
          </div>
          <div className="space-y-2">
            <div className="w-12 h-12 rounded-full bg-chapterly-purple/10 flex items-center justify-center mx-auto">
              <svg className="h-6 w-6 text-chapterly-purple" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
              </svg>
            </div>
            <h3 className="font-playfair font-semibold">For Writers</h3>
            <p className="text-sm text-muted-foreground">Share your stories and build your audience</p>
          </div>
          <div className="space-y-2">
            <div className="w-12 h-12 rounded-full bg-chapterly-purple/10 flex items-center justify-center mx-auto">
              <svg className="h-6 w-6 text-chapterly-purple" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <h3 className="font-playfair font-semibold">Community</h3>
            <p className="text-sm text-muted-foreground">Connect with readers and fellow writers</p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-sm text-muted-foreground mt-12">
          © {new Date().getFullYear()} ChapterSeed. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default ComingSoonPage;
